import { useEffect, useMemo, useState } from "react";
import { useAccount } from "../Context/AccountProvider";
import { AccountPageHeader } from "../components/AccountPageHeader";
import { ClockHistory } from "react-bootstrap-icons";
import { getPaymentHistory } from "../services/UserService";
import Items from "../services/PrizeNPCItemsService";
import { LoadingSpinner } from "../components/LoadingSpinner";
import { EmptyState } from "../components/EmptyState";
import { StatusBadge } from "../components/StatusBadge";

const PAGE_SIZE = 15;

const TABS = [
  { key: 'tebex',    label: 'Tebex'    },
  { key: 'payment',  label: 'PayPal'   },
  { key: 'prizenpc', label: 'PrizeNPC' },
];

// ─── Filter predicates (module-level = stable refs, safe in useMemo deps) ────

const paypalFilter = (r, q) =>
  r.payer_email?.toLowerCase().includes(q) ||
  r.txn_id?.toLowerCase().includes(q) ||
  String(r.payment_gross).includes(q) ||
  new Date(r.txn_date).toLocaleDateString().includes(q);

const tebexFilter = (r, q) =>
  r.payer_email?.toLowerCase().includes(q) ||
  r.transaction_id?.toLowerCase().includes(q) ||
  String(r.price_paid).includes(q) ||
  new Date(r.transaction_date).toLocaleDateString().includes(q);

const prizeNPCFilter = (r, q) =>
  Items[r.itemType]?.Name?.toLowerCase().includes(q) ||
  new Date(r.date).toLocaleDateString().includes(q);

// ─── Sort key extractors (module-level = stable refs) ─────────────────────────

const PAYPAL_SORTERS = {
  date:   r => new Date(r.txn_date).getTime(),
  email:  r => r.payer_email?.toLowerCase() ?? '',
  amount: r => r.payment_gross,
  coins:  r => r.donation_points,
  status: r => r.distributed,
};

const TEBEX_SORTERS = {
  date:   r => new Date(r.transaction_date).getTime(),
  email:  r => r.payer_email?.toLowerCase() ?? '',
  amount: r => r.price_paid,
  coins:  r => r.coins_bought,
  status: r => r.distributed?.data?.[0] ?? 0,
};

const PRIZENPC_SORTERS = {
  date:     r => new Date(r.date).getTime(),
  item:     r => Items[r.itemType]?.Name?.toLowerCase() ?? '',
  quantity: r => r.itemQuantity,
  coins:    r => (Items[r.itemType]?.DPSCost ?? 0) * r.itemQuantity,
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

function useTableControls(records, filterFn, sorters, defaultSortKey = null, defaultSortDir = 'desc') {
  const [query,   setQuery]   = useState('');
  const [page,    setPage]    = useState(1);
  const [sortKey, setSortKey] = useState(defaultSortKey);
  const [sortDir, setSortDir] = useState(defaultSortDir);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? records.filter(r => filterFn(r, q)) : records;
  }, [records, query, filterFn]);

  const sorted = useMemo(() => {
    const getter = sorters?.[sortKey];
    if (!getter) return filtered;
    return [...filtered].sort((a, b) => {
      const av = getter(a), bv = getter(b);
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filtered, sortKey, sortDir, sorters]);

  // Reset to page 1 whenever filter or sort changes
  useEffect(() => { setPage(1); }, [query, sortKey, sortDir]);

  function handleSort(key) {
    if (key === sortKey) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  }

  const pageCount = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const safePage  = Math.min(page, pageCount);
  const start     = (safePage - 1) * PAGE_SIZE;

  return {
    query, setQuery,
    page: safePage, setPage,
    pageCount,
    pageItems:     sorted.slice(start, start + PAGE_SIZE),
    totalFiltered: sorted.length,
    total:         records.length,
    sortKey, sortDir, handleSort,
  };
}

// ─── Shared UI components ─────────────────────────────────────────────────────

function SortableHeader({ label, colKey, sortKey, sortDir, onSort }) {
  const active = colKey === sortKey;
  return (
    <th
      className={`ph-th-sortable${active ? ' ph-th-active' : ''}`}
      onClick={() => onSort(colKey)}
    >
      {label}
      <span className="ph-sort-icon">
        {active ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}
      </span>
    </th>
  );
}

function Pagination({ page, pageCount, onPage }) {
  if (pageCount <= 1) return null;
  return (
    <div className="ph-pagination">
      <button className="ph-page-btn" onClick={() => onPage(1)}         disabled={page === 1}>«</button>
      <button className="ph-page-btn" onClick={() => onPage(page - 1)} disabled={page === 1}>‹</button>
      <span className="ph-page-info">Page {page} of {pageCount}</span>
      <button className="ph-page-btn" onClick={() => onPage(page + 1)} disabled={page === pageCount}>›</button>
      <button className="ph-page-btn" onClick={() => onPage(pageCount)} disabled={page === pageCount}>»</button>
    </div>
  );
}

function TableControls({ query, setQuery, totalFiltered, total, placeholder }) {
  return (
    <div className="ph-controls">
      <input
        type="text"
        className="ph-search-input"
        placeholder={placeholder}
        value={query}
        onChange={e => setQuery(e.target.value)}
      />
      <span className="ph-result-count">
        {totalFiltered !== total ? `${totalFiltered} of ${total}` : total}
        {' '}result{total !== 1 ? 's' : ''}
      </span>
    </div>
  );
}

// ─── Tab components ───────────────────────────────────────────────────────────

function PayPalTab({ records }) {
  const { query, setQuery, page, setPage, pageCount, pageItems, totalFiltered, total, sortKey, sortDir, handleSort } =
    useTableControls(records, paypalFilter, PAYPAL_SORTERS, 'date', 'desc');

  if (records.length === 0) {
    return (
      <EmptyState
        message="No PayPal transactions yet."
        ctaLabel="Purchase Paragon Coins"
        ctaTo="/buycoins"
      />
    );
  }

  const sumDollars = records.reduce((sum, x) => sum + x.payment_gross, 0);
  const sumCoins   = records.reduce((sum, x) => sum + x.donation_points, 0);
  const sortProps  = { sortKey, sortDir, onSort: handleSort };

  return (
    <>
      <TableControls
        query={query} setQuery={setQuery}
        totalFiltered={totalFiltered} total={total}
        placeholder="Search by email, amount, or transaction ID…"
      />
      <table className="ph-table">
        <thead>
          <tr>
            <SortableHeader label="Date"   colKey="date"   {...sortProps} />
            <SortableHeader label="Email"  colKey="email"  {...sortProps} />
            <SortableHeader label="Amount" colKey="amount" {...sortProps} />
            <SortableHeader label="Coins"  colKey="coins"  {...sortProps} />
            <SortableHeader label="Status" colKey="status" {...sortProps} />
          </tr>
        </thead>
        <tbody>
          {pageItems.length === 0
            ? <tr><td colSpan="5" className="ph-no-results">No results match your search.</td></tr>
            : pageItems.map((record, i) => {
                const date = new Date(record.txn_date);
                return (
                  <tr key={i}>
                    <td>
                      {date.toLocaleDateString()}
                      <span className="ph-cell-sub">{record.txn_id}</span>
                    </td>
                    <td className="ph-cell-muted">{record.payer_email}</td>
                    <td>${record.payment_gross}</td>
                    <td>{record.donation_points}</td>
                    <td><StatusBadge status={record.distributed === "1" ? "credited" : "pending"} /></td>
                  </tr>
                );
              })
          }
        </tbody>
        <tfoot>
          <tr>
            <td colSpan="2">All-time total</td>
            <td>${sumDollars}</td>
            <td>{sumCoins} Coins</td>
            <td />
          </tr>
        </tfoot>
      </table>
      <div className="ph-table-footer">
        <Pagination page={page} pageCount={pageCount} onPage={setPage} />
        {sumDollars > 0 && <p className="ph-thanks">Thank you for supporting Paragon Conquer!</p>}
      </div>
    </>
  );
}

function TebexTab({ records }) {
  const { query, setQuery, page, setPage, pageCount, pageItems, totalFiltered, total, sortKey, sortDir, handleSort } =
    useTableControls(records, tebexFilter, TEBEX_SORTERS, 'date', 'desc');

  if (records.length === 0) {
    return (
      <EmptyState
        message="No Tebex transactions yet."
        ctaLabel="Purchase Paragon Coins"
        ctaTo="/buycoins"
      />
    );
  }

  const sumDollars = records.reduce((sum, x) => sum + x.price_paid, 0);
  const sumCoins   = records.reduce((sum, x) => sum + x.coins_bought, 0);
  const sortProps  = { sortKey, sortDir, onSort: handleSort };

  return (
    <>
      <TableControls
        query={query} setQuery={setQuery}
        totalFiltered={totalFiltered} total={total}
        placeholder="Search by email, amount, or transaction ID…"
      />
      <table className="ph-table">
        <thead>
          <tr>
            <SortableHeader label="Date"   colKey="date"   {...sortProps} />
            <SortableHeader label="Email"  colKey="email"  {...sortProps} />
            <SortableHeader label="Amount" colKey="amount" {...sortProps} />
            <SortableHeader label="Coins"  colKey="coins"  {...sortProps} />
            <SortableHeader label="Status" colKey="status" {...sortProps} />
          </tr>
        </thead>
        <tbody>
          {pageItems.length === 0
            ? <tr><td colSpan="5" className="ph-no-results">No results match your search.</td></tr>
            : pageItems.map((record, i) => {
                const date = new Date(record.transaction_date);
                return (
                  <tr key={i}>
                    <td>
                      {date.toLocaleDateString()}
                      <span className="ph-cell-sub">{record.transaction_id}</span>
                    </td>
                    <td className="ph-cell-muted">{record.payer_email}</td>
                    <td>${record.price_paid}</td>
                    <td>{record.coins_bought}</td>
                    <td><StatusBadge status={record.distributed.data[0] === 1 ? "credited" : "pending"} /></td>
                  </tr>
                );
              })
          }
        </tbody>
        <tfoot>
          <tr>
            <td colSpan="2">All-time total</td>
            <td>${sumDollars}</td>
            <td>{sumCoins} Coins</td>
            <td />
          </tr>
        </tfoot>
      </table>
      <div className="ph-table-footer">
        <Pagination page={page} pageCount={pageCount} onPage={setPage} />
        {sumDollars > 0 && <p className="ph-thanks">Thank you for supporting Paragon Conquer!</p>}
      </div>
    </>
  );
}

function PrizeNPCTab({ records }) {
  const { query, setQuery, page, setPage, pageCount, pageItems, totalFiltered, total, sortKey, sortDir, handleSort } =
    useTableControls(records, prizeNPCFilter, PRIZENPC_SORTERS, 'date', 'desc');

  if (records.length === 0) {
    return (
      <EmptyState
        message="No PrizeNPC purchases yet."
        description="You haven't spent any Paragon Coins in-game yet."
        ctaLabel="Purchase Paragon Coins"
        ctaTo="/buycoins"
      />
    );
  }

  const sumCoinsSpent = records.reduce(
    (sum, r) => sum + Items[r.itemType].DPSCost * r.itemQuantity,
    0
  );
  const sortProps = { sortKey, sortDir, onSort: handleSort };

  return (
    <>
      <TableControls
        query={query} setQuery={setQuery}
        totalFiltered={totalFiltered} total={total}
        placeholder="Search by item name or date…"
      />
      <table className="ph-table">
        <thead>
          <tr>
            <SortableHeader label="Date"     colKey="date"     {...sortProps} />
            <SortableHeader label="Item"     colKey="item"     {...sortProps} />
            <SortableHeader label="Quantity" colKey="quantity" {...sortProps} />
            <SortableHeader label="Coins Spent" colKey="coins" {...sortProps} />
          </tr>
        </thead>
        <tbody>
          {pageItems.length === 0
            ? <tr><td colSpan="4" className="ph-no-results">No results match your search.</td></tr>
            : pageItems.map((record, i) => {
                const date = new Date(record.date);
                const cost = Items[record.itemType].DPSCost * record.itemQuantity;
                return (
                  <tr key={i}>
                    <td>{date.toLocaleDateString()}</td>
                    <td>{Items[record.itemType].Name}</td>
                    <td>{record.itemQuantity}</td>
                    <td>{cost}</td>
                  </tr>
                );
              })
          }
        </tbody>
        <tfoot>
          <tr>
            <td colSpan="3">All-time total</td>
            <td>{sumCoinsSpent} Coins</td>
          </tr>
        </tfoot>
      </table>
      <div className="ph-table-footer">
        <Pagination page={page} pageCount={pageCount} onPage={setPage} />
        {sumCoinsSpent > 0 && <p className="ph-thanks">Thank you for supporting Paragon Conquer!</p>}
      </div>
    </>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

function PaymentLogs() {
  const { accountInfo } = useAccount();
  const [activeTab, setActiveTab] = useState("tebex");
  const [paymentHistory, setPaymentHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const data = await getPaymentHistory(accountInfo.user.id);
        if (!cancelled) setPaymentHistory(data.result);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return <LoadingSpinner message={`Loading purchase history for ${accountInfo.user.name}…`} />;
  }

  if (error) {
    return (
      <div className="loading-container">
        <p>Failed to load purchase history: {error}</p>
      </div>
    );
  }

  return (
    <div className="ph-page">
      <AccountPageHeader
        icon={<ClockHistory />}
        title="Purchase History"
        description="A detailed record of your purchases and in-game spending"
      />

      <div className="ph-card">
        <div className="ph-tabs">
          {TABS.map(({ key, label }) => (
            <button
              key={key}
              className={`ph-tab${activeTab === key ? ' ph-tab--active' : ''}`}
              onClick={() => setActiveTab(key)}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="ph-tab-content">
          {activeTab === 'payment'  && <PayPalTab   records={paymentHistory[0]} />}
          {activeTab === 'tebex'    && <TebexTab    records={paymentHistory[2]} />}
          {activeTab === 'prizenpc' && <PrizeNPCTab records={paymentHistory[1]} />}
        </div>
      </div>
    </div>
  );
}

export { PaymentLogs };
